import * as React from 'react';

import { connect, useDispatch } from 'react-redux';
import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';

import Dinero from 'dinero.js';

import styled from 'styled-components';

// Services
import { PayoutHistory } from '@services/royalties/types/payout-history';
import { EarningsOwnerType } from '@services/earnings/types/earnings-owner-type';

// Components
import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import QuestionMarkHelper from '@components/basics/question-mark-helper';
import Button from '@components/actions/button';

// Controllers
import UserWithdrawMenu from '@webapp/controllers/menus/withdraw-menu';
import { push } from 'connected-react-router';
import { rgba } from 'polished';
import { ConnectedAccount } from '@services/stripe-connect/types/connected-account';
import {
  MIN_US_TRANSFER_OBJECT,
  MIN_INTL_TRANSFER_OBJECT,
} from '@services/stripe-connect/constants';
import { isAccountFromCountry } from '@services/stripe-connect/lib/is-account-from-country';
import { isConnectedAccountReady } from '../../../../../store/stripe-connect/selectors';

export interface AccountType {
  title: string;
  address: string;
  type: string;
  currency: string;
}

interface WalletTotalCtrlProps {
  className?: string;
  earningsKey: EarningsOwnerType;
}

interface StateProps {
  available: Dinero.Dinero;
  payoutTotal: Dinero.Dinero;
  payoutHistory: PayoutHistory[];
  isMobile: boolean;
  account?: ConnectedAccount;
}

const mapStateToProps = (state: AppState): StateProps => ({
  available: state.royalties.balance,
  payoutTotal: state.royalties.paid,
  payoutHistory: state.royalties.payouts,
  isMobile: state.ui.mobile,
  account: state.stripeConnect.account,
});

export const WalletTotalCtrl: React.FC<WalletTotalCtrlProps & StateProps> = (
  props
) => {
  const dispatch = useDispatch();

  const goToWalletHistory = () => {
    return dispatch(push(`/user/banking/history`));
  };

  // TODO: DEVTEAM-845 - Integrate wallet history
  const hasWalletHistory = true;

  const isDisabled = () => {
    if (!props.account) {
      return true;
    }

    if (!isConnectedAccountReady(props.account)) {
      return true;
    }

    if (isAccountFromCountry(props.account, 'US')) {
      return props.available.lessThan(Dinero(MIN_US_TRANSFER_OBJECT));
    }

    if (isAccountFromCountry(props.account, 'NG')) {
      return props.available.lessThan(Dinero(MIN_US_TRANSFER_OBJECT));
    }

    return props.available.lessThan(Dinero(MIN_INTL_TRANSFER_OBJECT));
  };

  return (
    <div className={props.className}>
      <Box noBorderBottom>
        <FundsWrapper
          flexDirection={props.isMobile ? 'column' : 'row'}
          justifyContent="space-between"
        >
          <div className="funds-title">
            <Flex alignItems="center" gap={5} className="funds-content-wrapper">
              <Typography type="section-title" customFont="medium">
                <Translate id="USER_PROFILE.WALLET.FUNDS" />
              </Typography>
              <QuestionMarkHelper id="funds">
                <Translate id="USER_PROFILE.WALLET.FUNDS.HELPER" />
              </QuestionMarkHelper>
            </Flex>
            <Typography
              type="subheader"
              customFont="semi-bold"
              className="mt-5"
            >
              {props.available.toFormat()}
            </Typography>
          </div>
          {!isConnectedAccountReady(props.account) ? (
            <GreyButton
              buttonType="primary"
              uppercase
              autoWidth
              customHeight={50}
              disabled
            >
              <Translate id="USER_PROFILE.WALLET.FUNDS.BUTTON" />
            </GreyButton>
          ) : (
            <UserWithdrawMenu disabled={isDisabled()} />
          )}
        </FundsWrapper>
        <Link
          className={!hasWalletHistory ? 'disabled' : ''}
          weight="700"
          onClick={goToWalletHistory}
        >
          <Translate id="USER_PROFILE.WALLET.FUNDS.HISTORY" />
        </Link>
      </Box>
    </div>
  );
};

export default connect(mapStateToProps)(styled(WalletTotalCtrl)`
  display: grid;
  grid-gap: 3px;
`);

const FundsWrapper = styled(Flex)`
  margin-bottom: 20px;

  .funds-title {
    margin-bottom: 20px;

    .funds-content-wrapper {
      position: relative;
    }

    @media ${({ theme }) => theme.device.tablet} {
      margin-bottom: 0;
    }
  }
`;

const Link = styled(Typography)`
  cursor: pointer;
  display: inline-block;
  color: ${({ theme }) => theme.colors.primary};
  transition: color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &.disabled {
    color: ${({ theme }) => theme.colors.tertiary};
    pointer-events: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.tertiary};
  }
`;

const GreyButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.secondary};
  pointer-events: all !important;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
  span {
    color: ${({ theme }) => rgba(theme.colors.primary, 0.5)};
  }
`;
